import {
  CombatPhase,
  PlayerState,
  EnemyData,
  TurnAssignment,
  CombatResult,
  CardData,
  CardModifier,
  RunAbility,
  Buff,
  CardSlot,
  EnemyIntent,
} from '../types';
import { Deck } from '../cards/Deck';
import { getEnemyIntent, advanceEnemyPattern, executeEnemyIntent } from '../enemies/EnemyAI';
import { resolveTurn } from './DamageResolver';
import { BALANCE } from '../constants';
import { Relic } from '../relics/Relic';
import { getRng } from '../utils/Random';
import { getCurseById } from '../cards/CurseRegistry';
import { FEATURES } from '../features';
import { applyStanceStartTurn } from '../enemies/StanceSystem';
import { hasMutator, assignInitialMutator, ensureBossMutatorForPhase } from '../enemies/MutatorRegistry';
import { createSummonedAdd } from '../enemies/Enemy';
import { createCard } from '../cards/Card';
import { getIncomingDamageMultiplierForPlayer, tickStatusesStartTurn } from './StatusSystem';

interface ZoneCapBonuses {
  attack: number;
  defense: number;
  ability: number;
}

interface EnemyIntentView {
  enemyId: string;
  enemyName: string;
  intent: EnemyIntent;
  isPrimary: boolean;
}

const MIN_ZONE_CAP = 1;
const MAX_ZONE_CAP = 4;
const MAX_ADDS = 2;

const HEX_PULSE_CURSES = ['blood_tax', 'heavy_hand', 'backfire', 'cracked_guard', 'static_noise', 'fumble_runes', 'ashen_weight'];

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function formatIntent(intent: EnemyIntent): string {
  switch (intent.type) {
    case 'attack':
      return `Attack ${intent.value}`;
    case 'defend':
      return `Defend ${intent.value}`;
    case 'buff':
      return intent.description ?? 'Buff';
    case 'special':
      return intent.description ?? 'Special';
    case 'summon':
      return intent.description ?? 'Summon';
    default:
      return intent.description ?? intent.type;
  }
}

export class CombatManager {
  phase: CombatPhase = 'draw';
  player: PlayerState;
  enemy: EnemyData;
  deck: Deck;
  turnNumber: number = 0;
  lastResult: CombatResult | null = null;
  modifiers: Map<number, CardModifier>;
  relics: Relic[];
  abilities: RunAbility[];
  private curses: string[];
  private adds: EnemyData[] = [];
  private activeTargetId: string | null = null;
  private zoneCapBonuses: ZoneCapBonuses = { attack: 0, defense: 0, ability: 0 };
  private turnIntents = new Map<string, EnemyIntent>();
  private combatLog: string[] = [];
  private enemyTurnCounters = new Map<string, number>();
  private addSpawnCounter = 0;
  private userSelectedTarget = false;

  constructor(
    enemy: EnemyData,
    deckNumbers?: number[],
    hp?: number,
    maxHp?: number,
    modifiers?: Map<number, CardModifier>,
    permanentExhaust?: number[],
    relics?: Relic[],
    abilities?: RunAbility[],
    curses: string[] = [],
  ) {
    this.enemy = {
      ...enemy,
      statuses: enemy.statuses ?? [],
      mutators: enemy.mutators ?? [],
    };
    this.modifiers = modifiers ?? new Map();
    this.relics = relics ?? [];
    this.abilities = abilities ?? [];
    this.curses = [...curses];

    const numbers = deckNumbers ?? [...BALANCE.STARTING_DECK];
    const available = permanentExhaust
      ? numbers.filter(n => !permanentExhaust.includes(n))
      : numbers;
    this.deck = new Deck(available, this.modifiers, this.curses);

    this.player = {
      maxHp: maxHp ?? BALANCE.STARTING_HP,
      hp: hp ?? BALANCE.STARTING_HP,
      block: 0,
      deck: numbers,
      hand: [],
      drawPile: [],
      exhaustPile: [],
      permanentExhaust: permanentExhaust ?? [],
      buffs: [],
      statuses: [],
    };

    this.enemyTurnCounters.set(this.enemy.id, 0);
    this.activeTargetId = this.enemy.id;
    assignInitialMutator(this.enemy);
  }

  startTurn(): CardData[] {
    this.turnNumber++;
    this.phase = 'draw';

    this.player.block = 0;
    this.enemy.block = 0;
    for (const add of this.adds) {
      add.block = 0;
    }

    if (FEATURES.statusSystem) {
      tickStatusesStartTurn(this.player);
    }

    if (this.turnNumber === 1 && this.relics.some(r => r.id === 'lucky_coin')) {
      this.player.block = 3;
    }

    if (this.turnNumber === 1 && this.relics.some(r => r.id === 'digital_crown')) {
      this.player.buffs.push({ type: 'strength', value: 1, duration: 99 });
    }

    if (FEATURES.enemyStances) {
      for (const foe of this.getAllLivingEnemies()) {
        const logs = applyStanceStartTurn(foe);
        this.appendCombatLog(logs);
      }
    }

    if (FEATURES.eliteMutators) {
      ensureBossMutatorForPhase(this.enemy);
      for (const foe of this.getAllLivingEnemies()) {
        if (hasMutator(foe, 'fortress')) {
          foe.block += 6;
          this.appendCombatLog([`${foe.name} Fortress: +6 block`]);
        }
      }
    }

    const turnBuffs = this.consumeTurnStartBuffs([
      'draw_bonus',
      'draw_penalty',
      'zone_cap_attack',
      'zone_cap_defense',
      'zone_cap_ability',
      'zone_cap_all',
    ]);

    this.zoneCapBonuses = {
      attack: turnBuffs.zone_cap_attack + turnBuffs.zone_cap_all,
      defense: turnBuffs.zone_cap_defense + turnBuffs.zone_cap_all,
      ability: turnBuffs.zone_cap_ability + turnBuffs.zone_cap_all,
    };
    if (this.turnNumber === 1 && this.relics.some(r => r.id === 'tactical_manual')) {
      this.zoneCapBonuses.attack += 1;
      this.appendCombatLog(['Tactical Manual: ATK cap +1 on turn 1']);
    }
    if (this.turnNumber === 1 && this.relics.some(r => r.id === 'guard_manual')) {
      this.zoneCapBonuses.defense += 1;
      this.appendCombatLog(['Guard Manual: DEF cap +1 on turn 1']);
    }
    if (this.turnNumber === 1 && this.relics.some(r => r.id === 'focus_manual')) {
      this.zoneCapBonuses.ability += 1;
      this.appendCombatLog(['Focus Manual: ABL cap +1 on turn 1']);
    }

    let extraDraw = turnBuffs.draw_bonus - turnBuffs.draw_penalty;
    if (this.turnNumber === 1 && this.relics.some(r => r.id === 'card_magnet')) {
      extraDraw += 1;
    }
    if (this.turnNumber === 1 && this.relics.some(r => r.id === 'ember_totem')) {
      extraDraw += 1;
    }
    if (this.relics.some(r => r.id === 'tempo_core')) {
      extraDraw += 1;
    }

    const drawCount = clamp(BALANCE.HAND_SIZE + extraDraw, 1, 10);
    const drawn = this.deck.draw(drawCount);
    this.applyRunAbilities();
    this.applyCurseOnDrawEffects(drawn);
    this.player.hand = [...this.deck.hand];

    this.pruneDefeatedAdds();
    this.syncActiveTarget();
    this.cacheTurnIntents();

    this.phase = 'assign';
    return drawn;
  }

  getAssignment(): TurnAssignment {
    const assignment: TurnAssignment = { attack: [], defense: [], ability: [] };
    for (const card of this.deck.hand) {
      if (card.slot === 'attack') assignment.attack.push(card);
      else if (card.slot === 'defense') assignment.defense.push(card);
      else if (card.slot === 'ability') assignment.ability.push(card);
    }
    return assignment;
  }

  canEndTurn(): boolean {
    const assignment = this.getAssignment();
    const totalAssigned = assignment.attack.length + assignment.defense.length + assignment.ability.length;
    if (totalAssigned >= 1) return true;
    // Soft-lock guard: if the player has no number card they could assign
    // (empty hand, or a hand of only unplayable curse cards) let them end the
    // turn anyway so a run can never get permanently stuck.
    const hasAssignableCard = this.deck.hand.some(c => c.slot === 'hand' && c.kind === 'number');
    return !hasAssignableCard;
  }

  getZoneCap(slot: Exclude<CardSlot, 'hand'>): number {
    const bonus = this.zoneCapBonuses[slot] ?? 0;
    const relicBonus = this.relics.some(r => r.id === 'triune_matrix') ? 1 : 0;
    return clamp(MIN_ZONE_CAP + bonus + relicBonus, MIN_ZONE_CAP, MAX_ZONE_CAP);
  }

  getZoneCaps(): ZoneCapBonuses {
    return {
      attack: this.getZoneCap('attack'),
      defense: this.getZoneCap('defense'),
      ability: this.getZoneCap('ability'),
    };
  }

  getNextDrawPreview(): { bonus: number; penalty: number } {
    let bonus = 0;
    let penalty = 0;
    for (const buff of this.player.buffs) {
      if (buff.type === 'draw_bonus') bonus += buff.value;
      if (buff.type === 'draw_penalty') penalty += buff.value;
    }
    return { bonus, penalty };
  }

  getEnemyIntentViews(): EnemyIntentView[] {
    const enemies = this.getAllLivingEnemies();
    return enemies.map(foe => ({
      enemyId: foe.id,
      enemyName: foe.name,
      intent: this.turnIntents.get(foe.id) ?? getEnemyIntent(foe),
      isPrimary: foe.id === this.enemy.id,
    }));
  }

  getAdds(): EnemyData[] {
    return [...this.adds];
  }

  getActiveTargetId(): string {
    this.syncActiveTarget();
    return this.activeTargetId ?? this.enemy.id;
  }

  setActiveTarget(targetId: string): boolean {
    if (!FEATURES.limitedSummons) return false;
    const alive = this.getAllLivingEnemies();
    if (!alive.some(enemy => enemy.id === targetId)) return false;
    this.activeTargetId = targetId;
    this.userSelectedTarget = true;
    return true;
  }

  getCombatLog(): string[] {
    return this.combatLog.slice(-5);
  }

  endTurn(): CombatResult | null {
    if (!this.canEndTurn()) return null;

    this.phase = 'resolving';
    const assignment = this.getAssignment();
    const primaryIntent = this.turnIntents.get(this.enemy.id) ?? getEnemyIntent(this.enemy);
    const targetEnemy = this.getActiveTargetEnemy();

    const result = resolveTurn(
      assignment,
      this.player,
      this.enemy,
      primaryIntent,
      this.relics,
      { targetEnemy },
    );
    this.lastResult = result;

    if (FEATURES.limitedSummons && this.enemy.hp > 0 && primaryIntent.type === 'summon') {
      this.spawnSummonedAdd(primaryIntent.summonTemplateId ?? this.enemy.summonTemplateId, result);
    }

    this.pruneDefeatedAdds();

    for (const add of this.adds) {
      if (add.hp <= 0) continue;

      const addIntent = this.turnIntents.get(add.id) ?? getEnemyIntent(add);
      const addResult = executeEnemyIntent(add, addIntent, this.player.block, this.player);

      let addDamage = addResult.damageToPlayer;
      if (FEATURES.statusSystem) {
        addDamage = Math.floor(addDamage * getIncomingDamageMultiplierForPlayer(this.player));
      }

      if (this.relics.some(r => r.id === 'glass_cannon') && addDamage > 0) {
        addDamage += 1;
      }

      result.playerDamage += addDamage;
      this.player.hp -= addDamage;
      add.block += addResult.enemyBlockGain;

      if (addResult.combatLog && addResult.combatLog.length > 0) {
        result.combatLog?.push(...addResult.combatLog);
      }
    }

    for (const foe of this.getAllLivingEnemies()) {
      this.applyHexPulseIfReady(foe, result);
      if (FEATURES.statusSystem) {
        tickStatusesStartTurn(foe);
      }
    }

    for (const card of [...assignment.ability]) {
      if (card.permanentExhaust) {
        this.deck.permanentlyExhaust(card);
        if (card.kind === 'number') {
          this.player.permanentExhaust.push(card.number);
        }
      }
    }

    this.deck.discardHand();

    advanceEnemyPattern(this.enemy);
    for (const add of this.adds) {
      advanceEnemyPattern(add);
    }

    this.pruneDefeatedAdds();
    this.syncActiveTarget();

    if (result.combatLog && result.combatLog.length > 0) {
      this.appendCombatLog(result.combatLog);
    }
    result.combatLog = this.getCombatLog();
    result.activeTargetId = this.getActiveTargetId();

    this.phase = 'check_end';
    if (this.enemy.hp <= 0) {
      this.phase = 'victory';
    } else if (this.player.hp <= 0) {
      this.phase = 'defeat';
    }

    return result;
  }

  get currentIntent() {
    return this.turnIntents.get(this.enemy.id) ?? getEnemyIntent(this.enemy);
  }

  get isOver(): boolean {
    return this.phase === 'victory' || this.phase === 'defeat';
  }

  private cacheTurnIntents(): void {
    this.turnIntents.clear();

    const beforePhase = this.enemy.phase ?? 1;
    const primaryIntent = getEnemyIntent(this.enemy);
    this.turnIntents.set(this.enemy.id, primaryIntent);

    if (FEATURES.bossPhaseScripts && this.enemy.boss) {
      const afterPhase = this.enemy.phase ?? 1;
      if (afterPhase !== beforePhase) {
        this.appendCombatLog([`${this.enemy.name} shifts to Phase ${afterPhase}`]);
      }
      ensureBossMutatorForPhase(this.enemy);
    }

    for (const add of this.adds) {
      if (add.hp > 0) {
        this.turnIntents.set(add.id, getEnemyIntent(add));
      }
    }
  }

  private getAllLivingEnemies(): EnemyData[] {
    return [this.enemy, ...this.adds].filter(enemy => enemy.hp > 0);
  }

  private getActiveTargetEnemy(): EnemyData {
    this.syncActiveTarget();
    if (this.activeTargetId === this.enemy.id) return this.enemy;
    return this.adds.find(add => add.id === this.activeTargetId && add.hp > 0) ?? this.enemy;
  }

  private syncActiveTarget(): void {
    const adds = this.adds.filter(add => add.hp > 0);

    if (!FEATURES.limitedSummons || adds.length === 0) {
      this.activeTargetId = this.enemy.id;
      this.userSelectedTarget = false;
      return;
    }

    if (this.activeTargetId && [this.enemy, ...adds].some(enemy => enemy.id === this.activeTargetId && enemy.hp > 0)) {
      if (!this.userSelectedTarget) {
        this.activeTargetId = adds[0].id;
      }
      return;
    }

    this.activeTargetId = adds[0].id;
    this.userSelectedTarget = false;
  }

  private spawnSummonedAdd(templateId: string | undefined, result: CombatResult): void {
    if (!FEATURES.limitedSummons || !templateId) return;
    if (this.adds.filter(add => add.hp > 0).length >= MAX_ADDS) return;

    const add = createSummonedAdd(templateId);
    add.id = `${add.id}_${this.turnNumber}_${this.addSpawnCounter++}`;
    add.statuses = [];
    add.mutators = [];
    this.adds.push(add);
    this.enemyTurnCounters.set(add.id, 0);
    this.turnIntents.set(add.id, getEnemyIntent(add));
    this.appendCombatLog([`${this.enemy.name} summons ${add.name}`]);
    result.combatLog?.push(`${this.enemy.name} summons ${add.name}`);
  }

  private pruneDefeatedAdds(): void {
    if (this.adds.length === 0) return;
    this.adds = this.adds.filter(add => add.hp > 0);
    const activeIds = new Set([this.enemy.id, ...this.adds.map(add => add.id)]);
    for (const id of [...this.turnIntents.keys()]) {
      if (!activeIds.has(id)) {
        this.turnIntents.delete(id);
      }
    }
  }

  private appendCombatLog(lines: string[]): void {
    if (lines.length === 0) return;
    this.combatLog.push(...lines);
    if (this.combatLog.length > 40) {
      this.combatLog = this.combatLog.slice(-40);
    }
  }

  private applyHexPulseIfReady(enemy: EnemyData, result: CombatResult): void {
    if (!FEATURES.eliteMutators || !hasMutator(enemy, 'hex_pulse')) return;

    const turns = (this.enemyTurnCounters.get(enemy.id) ?? 0) + 1;
    this.enemyTurnCounters.set(enemy.id, turns);

    if (turns % 3 !== 0) return;

    const curseId = getRng().pick(HEX_PULSE_CURSES);
    const curse = getCurseById(curseId);
    const curseCard = createCard(0, [], {
      kind: 'curse',
      curseId,
      curseName: curse?.name ?? 'Hex Curse',
      curseText: curse?.text ?? 'A temporary curse from Hex Pulse.',
    });
    this.deck.discardPile.push(curseCard);

    const message = `${enemy.name} Hex Pulse injects ${curseCard.curseName} into discard`;
    result.combatLog?.push(message);
    this.appendCombatLog([message]);
  }

  private applyRunAbilities(): void {
    if (this.abilities.length === 0 || this.deck.hand.length === 0) return;
    const rng = getRng();
    for (const ability of this.abilities) {
      const candidates = this.deck.hand.filter(c => c.kind === 'number');
      if (candidates.length === 0) return;
      const target = rng.pick(candidates);
      target.modifiers.push({
        ...ability.modifier,
        effect: { ...ability.modifier.effect },
        temporary: true,
      });
    }
  }

  private applyCurseOnDrawEffects(drawn: CardData[]): void {
    for (const card of drawn) {
      if (card.kind !== 'curse' || !card.curseId) continue;
      const curse = getCurseById(card.curseId);
      if (!curse?.onDraw) continue;
      if (curse.onDraw.hpLoss) {
        this.player.hp = Math.max(0, this.player.hp - curse.onDraw.hpLoss);
        this.appendCombatLog([`${curse.name} triggers on draw: lose ${curse.onDraw.hpLoss} HP`]);
      }
      if (curse.onDraw.drawPenaltyNextTurn) {
        this.player.buffs.push({
          type: 'draw_penalty',
          value: curse.onDraw.drawPenaltyNextTurn,
          duration: 1,
        });
        this.appendCombatLog([`${curse.name} triggers on draw: next draw -${curse.onDraw.drawPenaltyNextTurn}`]);
      }
    }
  }

  private consumeTurnStartBuffs(
    types: Buff['type'][],
  ): Record<'draw_bonus' | 'draw_penalty' | 'zone_cap_attack' | 'zone_cap_defense' | 'zone_cap_ability' | 'zone_cap_all', number> {
    const totals = {
      draw_bonus: 0,
      draw_penalty: 0,
      zone_cap_attack: 0,
      zone_cap_defense: 0,
      zone_cap_ability: 0,
      zone_cap_all: 0,
    };

    const active = new Set(types);
    const remaining: Buff[] = [];

    for (const buff of this.player.buffs) {
      if (!active.has(buff.type)) {
        remaining.push(buff);
        continue;
      }

      if (buff.type in totals) {
        const key = buff.type as keyof typeof totals;
        totals[key] += buff.value;
      }

      const nextDuration = buff.duration - 1;
      if (nextDuration > 0) {
        remaining.push({ ...buff, duration: nextDuration });
      }
    }

    this.player.buffs = remaining;
    return totals;
  }

  debugEnemyIntentSummary(): string {
    const intents = this.getEnemyIntentViews()
      .map(view => `${view.enemyName}: ${formatIntent(view.intent)}`)
      .join(' | ');
    return intents;
  }
}
